import Phaser from 'phaser';
import { centerMowerIcon } from '../ui/mowerIcon';
import type { LevelDef } from '../../content/levelSchema';
import { MOWER_IDS, MOWERS, type MowerId } from '../../content/mowers';
import { ECONOMY, effectiveMower, engineInUse, mowerStatus, UPGRADES } from '../../meta/economy';
import { parUpgradesLacking } from '../../meta/par';
import { STAR } from '../../meta/save/schema';
import { Dir } from '../../sim/direction';
import { mowerTextureKey } from '../render/placeholderArt';
import { saves } from '../records';
import {
  bevelButton,
  clearScene,
  drawBigStar,
  lawnThumbnail,
  navyPanel,
  thumbnailScale,
} from '../ui/chrome';
import { mowerPreview, mowerShowcaseHeight } from '../ui/showcase';
import { label, UI } from '../ui/text';
import { wrapLineCount } from '../ui/wrap';
import { drawCheck, formatTime } from '../ui/widgets';
import type { EndlessRun, LevelSceneData, Playlist } from './LevelScene';
import { wipeTo } from './WipeScene';
import { DECORATIONS, hasTallGrass, tallGrassNote } from '../../content/tiles';
import { ownMowerTexture } from '../render/cosmeticArt';
import { starGoals } from '../starGoals';
import { rareFindOn } from '../rareFinds';
import { needsFullGame } from '../../meta/unlock';
import { campaignScope, lawnBoard } from '../../meta/online/boards';
import type { BoardResponse } from '../../meta/online/schema';
import { TICK_RATE } from '../../sim/constants';
import { featuredNow, loadBoard, onlineOn } from '../online';

export interface LevelCardData {
  level: LevelDef;
  /** Mowers the player may pick here (allowed by the lawn; owned unless `freePick`). */
  choices: MowerId[];
  /** Dev/test lawns and loaners: offer mowers regardless of ownership. */
  freePick?: boolean;
  /** Shown above the mower list, e.g. "Loaner mower". */
  mowerHeading?: string;
  playlist?: Playlist;
  daily?: string;
  endless?: EndlessRun;
  /** A run to race as a ghost (a community lawn's best). */
  ghost?: LevelSceneData['ghost'];
  /** Where Back goes, and what it is started with. */
  backTo?: string;
  backData?: object;
}

const REG_MOWER_CHOICE = 'card.mower';
const CHIP = 30;
const BUTTONS_H = 34;

/**
 * Pre-level card (docs/ART_DIRECTION.md): the lawn's mini map, its star goals, and which mower to
 * bring (chips; the picked one on show), with MOW! at the bottom in thumb reach.
 */
export class LevelCardScene extends Phaser.Scene {
  private data_!: LevelCardData;
  /** Online boards for this lawn by mower (a campaign lawn's, or the featured board), once read. */
  private boards = new Map<string, BoardResponse | null>();

  constructor() {
    super('LevelCard');
  }

  init(data: LevelCardData): void {
    this.data_ = data;
    this.boards = new Map();
  }

  /**
   * The online board this card shows a line from, for the picked mower: a campaign lawn's (one
   * per mower), or the featured board (any mower). Null for other lawns, or with online play off.
   */
  private boardFor(pick: MowerId | undefined): { board: string; scope: string } | null {
    const list = this.data_.playlist;
    if (!onlineOn() || !list || this.data_.daily) return null;
    if (list.featured) {
      const f = featuredNow();
      return f ? { board: f.board, scope: f.scope } : null;
    }
    if (list.kind !== 'campaign' || list.back || !pick) return null;
    const n = list.index + 1;
    const scope = campaignScope(n, pick);
    return scope ? { board: lawnBoard(n, pick), scope } : null;
  }

  /** The line for the board: the best time and the player's place, once loaded. */
  private boardNote(pick: MowerId | undefined): { text: string; color: number } | null {
    const at = this.boardFor(pick);
    if (!at) return null;
    const key = `${at.board}/${at.scope}`;
    if (!this.boards.has(key)) {
      this.boards.set(key, null);
      void loadBoard(at.board, at.scope).then((b) => {
        this.boards.set(key, b);
        if (b && this.scene.isActive()) this.redraw();
      });
      return null;
    }
    const board = this.boards.get(key);
    const best = board?.top[0];
    if (!board || !best) return null;
    const me = board.me;
    return {
      text: `Best ${formatTime(best.value, TICK_RATE)}${me ? `, you #${me.rank} of ${board.total}` : ''}`,
      color: UI.accent,
    };
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
  }

  private readonly redraw = (): void => {
    this.tweens.killAll();
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const { level, choices, freePick } = this.data_;
    const save = saves().save;
    const usable = choices.filter((id) => freePick || save.ownedMowers.includes(id));
    const remembered = this.registry.get(REG_MOWER_CHOICE) as MowerId | undefined;
    const pick =
      [remembered, save.selectedMower].find((id): id is MowerId => !!id && usable.includes(id)) ??
      usable[0];
    const g = this.add.graphics();
    const roomy = height >= 330;
    navyPanel(g, 4, 4, width - 8, height - BUTTONS_H - 10);

    // Title and size.
    // (A campaign lawn opened from the DEV tab keeps its own number.)
    const list = this.data_.playlist;
    // (A lawn of your own goes by its name alone.)
    const title = list?.featured
      ? `${list.featured.lawn} ${level.name}`
      : list && !list.back
        ? `${list.kind === 'test' ? (level.level ?? list.index + 1) : list.index + 1} ${level.name}`
        : level.name;
    const titleText = title.toUpperCase();
    label(this, width / 2, 10, titleText, 1, UI.accent)
      .setOrigin(0.5, 0)
      .setMaxWidth(width - 20)
      .setCenterAlign();
    const subY = 10 + wrapLineCount(titleText, width - 20) * 10 + 1;
    const critters = level.sim.critters?.length ?? 0;
    const sub = `${level.sim.width}x${level.sim.height} lawn${critters ? `  ${critters} critter${critters > 1 ? 's' : ''}` : ''}`;
    label(this, width / 2, subY, sub, 1, UI.dim).setOrigin(0.5, 0);
    const earned = save.levels[level.id]?.stars ?? 0;
    const done = (earned & STAR.clear) !== 0;

    // The objective (mowing it marks the lawn done) and its three star goals.
    // (The times are for the picked mower at its Tuned Engine level in use.)
    const { objective, goals } = starGoals(level, pick, pick ? engineInUse(save, pick) : 0);

    // What to know about this lawn, one note each.
    const notes: { text: string; color?: number }[] = [];
    // Late campaign lawns: par assumes the route upgrades (src/meta/par.ts); say which are missing
    // (at most 27 characters: "Par uses Sharpened Blades" fits on one line).
    const lacking =
      pick && level.level !== undefined && !this.data_.daily && level.par[pick] !== undefined
        ? parUpgradesLacking(save, pick, level.level)
        : [];
    if (lacking.length === 1) {
      notes.push({ text: `Par uses ${UPGRADES[lacking[0]!].name}`, color: UI.accent });
    } else if (lacking.length > 1) {
      notes.push({ text: `Par uses ${lacking.length} Garage upgrades`, color: UI.accent });
    }
    if (pick && hasTallGrass(level.art)) {
      const bite = this.data_.daily ? MOWERS[pick].bite : effectiveMower(save, pick).bite;
      notes.push({ text: tallGrassNote(bite) });
    }
    if (level.art.includes('flowerBed'))
      notes.push({ text: `Flowers: -${ECONOMY.trampleCost} coin each` });
    if (level.art.includes('green'))
      notes.push({ text: `Green: keep off, -${ECONOMY.trampleCost} each` });
    if (level.sim.moles) notes.push({ text: 'Moles: flatten molehills' });
    if (level.sim.hills) notes.push({ text: 'Hills: slow up, fast down' });
    if (level.sim.balls) notes.push({ text: 'Stray balls: whistle, then dodge' });
    if ((level.sim.critters ?? []).some((c) => c.kind === 'dog'))
      notes.push({ text: `Dog: a bump = heart, -${ECONOMY.dogBumpCost}` });
    if (level.art.includes('sprinkler'))
      notes.push({ text: `Sprinklers: wet = no turns, -${ECONOMY.sprinklerCost}` });
    if (level.art.includes('hose'))
      notes.push({ text: `Hoses: -${ECONOMY.hoseCost} per chewed bit` });
    if (level.art.some((id) => DECORATIONS.includes(id)))
      notes.push({ text: 'Smashing things costs coins' });
    if (level.art.includes('meadowGrass')) notes.push({ text: 'Meadow grows back if half mowed' });
    if (level.art.includes('beehive'))
      notes.push({ text: `Beehives: a sting and -${ECONOMY.hiveCost}` });
    // Online: the lawn's best time (with this mower) and the player's place.
    const online = this.boardNote(pick);
    if (online) notes.unshift(online);
    if (list?.featured) {
      notes.unshift({ text: 'Weekly: top 10 wins coins', color: UI.text });
    }
    const lawnNo = this.data_.playlist?.kind === 'campaign' ? this.data_.playlist.index + 1 : null;
    const find = lawnNo !== null && !this.data_.daily ? rareFindOn(save, lawnNo) : null;
    if (find) {
      const owned = save.rareFinds.includes(find.item);
      notes.push({
        text: owned
          ? 'Rare find: already yours'
          : find.wandering
            ? 'A rare find wandered here!'
            : 'Rare find! Clear in one go.',
        color: owned ? UI.dim : UI.accent,
      });
    }

    // Everything wraps; the mini map takes the room that is left (none on a crowded short card).
    const goalW = width - 36;
    const noteW = width - 28;
    const ids = freePick ? choices : MOWER_IDS;
    const gap = 4;
    // Mower chips as big as they come, smaller once there are more than fit across the card.
    const size = Math.min(CHIP, Math.floor((width - 16 - (ids.length - 1) * gap) / ids.length));
    const bottomOfPanel = height - BUTTONS_H - 10;
    const goalsH = [objective, ...goals.map(([, t]) => t)].reduce(
      (h, t) => h + wrapLineCount(t, goalW) * 10 + 1,
      0,
    );
    const tight = (spacing: number) =>
      notes.reduce((h, n) => h + wrapLineCount(n.text, noteW) * 10 + spacing, 0);
    const fixed = subY + 12 + goalsH + 4 + 11 + size + 6 + 10;
    const noteGap = fixed + tight(2) + 8 + 24 <= bottomOfPanel ? 2 : 0;
    const room = bottomOfPanel - fixed - tight(noteGap) - 8;
    const thumbMax = Math.min(roomy ? 72 : 44, room);
    let y = subY + 12;
    // (At least a pixel a tile: a lawn too long even so, Rain Chase's 240 rows, goes without.)
    const fits = level.sim.height <= thumbMax && level.sim.width <= width - 40;
    if (thumbMax >= 20 && fits) {
      const px = thumbnailScale(level, width - 40, thumbMax);
      const thumb = this.add
        .image(width / 2, y, lawnThumbnail(this, level, px, done))
        .setOrigin(0.5, 0);
      g.lineStyle(1, UI.rule).strokeRect(
        Math.round(thumb.x - thumb.width / 2) - 1.5,
        thumb.y - 1.5,
        thumb.width + 3,
        thumb.height + 3,
      );
      y = thumb.y + thumb.height + 8;
    }

    drawCheck(g, 14, y, done);
    label(this, 26, y, objective, 1, done ? UI.good : UI.text).setMaxWidth(goalW);
    y += wrapLineCount(objective, goalW) * 10 + 1;
    for (const [bit, text] of goals) {
      drawBigStar(g, 14, y, (earned & bit) !== 0);
      label(this, 26, y, text).setMaxWidth(goalW);
      y += wrapLineCount(text, goalW) * 10 + 1;
    }
    y += noteGap ? 1 : 0;
    for (const n of notes) {
      label(this, 14, y, n.text, 1, n.color ?? UI.dim).setMaxWidth(noteW);
      y += wrapLineCount(n.text, noteW) * 10 + noteGap;
    }

    // Mower chips.
    y += 4;
    label(this, 14, y, this.data_.mowerHeading ?? 'MOWER', 1, UI.dim);
    y += 11;
    const x0 = Math.round((width - (ids.length * size + (ids.length - 1) * gap)) / 2);
    ids.forEach((id, i) =>
      this.drawChip(g, x0 + i * (size + gap), y, size, id, id === pick, usable, choices),
    );
    y += size + 6;

    // The picked mower on show (room permitting: its stats column wraps, so measure it), else
    // its blurb, if that fits.
    const stats = pick && !this.data_.daily ? effectiveMower(save, pick) : undefined;
    const previewH = pick ? mowerShowcaseHeight(width - 24, stats ?? MOWERS[pick]) : 0;
    const blurbH = pick ? wrapLineCount(MOWERS[pick].blurb, width - 28) * 10 - 3 : 0;
    if (pick && roomy && y + previewH <= bottomOfPanel - 2) {
      mowerPreview(
        this,
        12,
        y,
        width - 24,
        pick,
        stats ? { stats, texture: ownMowerTexture(this, pick, save) } : {},
      );
    } else if (pick && y + blurbH <= bottomOfPanel - 2) {
      label(this, 14, y, MOWERS[pick].blurb, 1, UI.dim).setMaxWidth(width - 28);
    }

    // BACK and MOW! at the bottom, in thumb reach.
    const by = height - BUTTONS_H;
    bevelButton(this, 6, by + 4, 56, 22, 'BACK', () =>
      this.scene.start(this.data_.backTo ?? 'Menu', this.data_.backData ?? {}),
    );
    // A campaign lawn past the free ones, without the full game: the offer instead of MOW!.
    const paid = list?.kind === 'campaign' && !list.back && needsFullGame(save, list.index + 1);
    if (paid) {
      bevelButton(
        this,
        68,
        by,
        width - 74,
        28,
        'UNLOCK',
        () => wipeTo(this, 'Unlock', {}),
        true,
        2,
      );
    } else if (pick) {
      bevelButton(this, 68, by, width - 74, 28, 'MOW!', () => this.start(pick), true, 2);
    }
  }

  /** A mower chip: tap to bring it. Locked mowers are silhouettes; unowned ones are dimmed. */
  private drawChip(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    size: number,
    id: MowerId,
    picked: boolean,
    usable: MowerId[],
    choices: MowerId[],
  ): void {
    const CHIP = size;
    const save = saves().save;
    const ok = usable.includes(id);
    const allowedHere = choices.includes(id);
    g.fillStyle(UI.shadow).fillRect(x - 1, y - 1, CHIP + 2, CHIP + 2);
    g.fillStyle(ok ? 0x1d5a25 : 0x2a2550).fillRect(x, y, CHIP, CHIP);
    if (picked) {
      g.lineStyle(1, UI.accent).strokeRect(x - 1.5, y - 1.5, CHIP + 3, CHIP + 3);
    }
    const img = this.add.image(
      x + CHIP / 2,
      y + CHIP / 2,
      this.data_.daily ? mowerTextureKey(id) : ownMowerTexture(this, id, save),
      Dir.E,
    );
    img.setScale(Math.min(1, (CHIP - 2) / img.width)); // fit the chip (the Fairway Mower is wide)
    centerMowerIcon(img, id);
    const locked = !this.data_.freePick && mowerStatus(save, id) === 'locked';
    if (locked) img.setTint(UI.shadow).setTintMode(Phaser.TintModes.FILL).setAlpha(0.8);
    else if (!ok) img.setAlpha(0.35);
    if (!allowedHere && !this.data_.freePick) {
      // Not allowed on this lawn: a small bar across the chip.
      g.fillStyle(UI.danger).fillRect(x + 3, y + CHIP - 5, CHIP - 6, 2);
    }
    if (!ok) return;
    this.add
      .zone(x, y, CHIP, CHIP)
      .setOrigin(0)
      .setData('label', MOWERS[id].name)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        this.registry.set(REG_MOWER_CHOICE, id);
        this.redraw();
      });
  }

  private start(mowerId: MowerId): void {
    const { level, playlist, daily, endless, ghost } = this.data_;
    if (!daily) saves().update((s) => (s.selectedMower = mowerId));
    const data: LevelSceneData = {
      level,
      mowerId,
      ...(playlist ? { playlist } : {}),
      ...(daily ? { daily } : {}),
      ...(endless ? { endless } : {}),
      ...(ghost ? { ghost } : {}),
    };
    wipeTo(this, 'Level', data);
  }
}
